import { useCallback, useEffect, useState } from "react";
import { UserPlus, FolderPlus, FileDown, Pencil, ShieldCheck, User as UserIcon } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import TopHeader from "@/components/TopHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { formatMinutesAsHours, formatCurrency } from "@/lib/format";

const ACTIVITIES = [
    "Anfahrt", "Baustelleneinrichtung", "Montage", "Elektroarbeiten",
    "Verkabelung", "Inbetriebnahme", "Fehlersuche", "Nacharbeit",
    "Dokumentation", "Sonstiges",
];

export default function Admin() {
    const [users, setUsers] = useState([]);
    const [projects, setProjects] = useState([]);
    const [creatingUser, setCreatingUser] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [creatingProject, setCreatingProject] = useState(false);
    const [editingProject, setEditingProject] = useState(null);

    const load = useCallback(async () => {
        try {
            const [u, p] = await Promise.all([
                api.get("/users"),
                api.get("/projects", { params: { include_archived: true } }),
            ]);
            setUsers(u.data || []); setProjects(p.data || []);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    return (
        <div>
            <TopHeader title="Admin" subtitle="Benutzer, Projekte & Reports" />
            <main className="mx-auto max-w-4xl px-4 pt-4 safe-bottom" data-testid="page-admin">
                <Tabs defaultValue="reports" className="w-full">
                    <TabsList className="grid w-full grid-cols-3 bg-mes-surfaceAlt p-1">
                        <TabsTrigger value="reports" data-testid="tab-reports">Reports</TabsTrigger>
                        <TabsTrigger value="projects" data-testid="tab-projects">Projekte</TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-users">Benutzer</TabsTrigger>
                    </TabsList>

                    <TabsContent value="reports" className="mt-4">
                        <ReportsPanel users={users} projects={projects} />
                    </TabsContent>

                    <TabsContent value="projects" className="mt-4">
                        <div className="mb-3 flex justify-end">
                            <Button data-testid="btn-new-project" onClick={() => setCreatingProject(true)} className="mes-btn-primary">
                                <FolderPlus className="mr-2 h-4 w-4" /> Neues Projekt
                            </Button>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                            {projects.map((p) => (
                                <Card key={p.id} className="mes-card p-4" data-testid={`admin-project-${p.id}`}>
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">{p.customer} · {p.status}</div>
                                            <div className="font-display text-[15px] font-semibold text-mes-ink">{p.name}</div>
                                            <div className="text-xs text-mes-muted">{p.address}</div>
                                            <div className="mt-2 flex gap-4 text-xs">
                                                <span><span className="text-mes-muted">Kontingent:</span> <span className="tabular font-semibold text-mes-ink">{formatMinutesAsHours((p.stundenkontingent || 0) * 60)} h</span></span>
                                                <span><span className="text-mes-muted">Satz Extra:</span> <span className="tabular font-semibold text-mes-brand">{formatCurrency(p.hourly_rate_extra)}</span></span>
                                            </div>
                                            <div className="mt-1 text-xs text-mes-muted">Zugewiesen: {(p.assigned_user_ids || []).length}</div>
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => setEditingProject(p)} data-testid={`btn-edit-project-${p.id}`}>
                                            <Pencil className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </Card>
                            ))}
                            {projects.length === 0 && (
                                <Card className="mes-card p-4 text-sm text-mes-muted md:col-span-2">Keine Projekte vorhanden.</Card>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="users" className="mt-4">
                        <div className="mb-3 flex justify-end">
                            <Button data-testid="btn-new-user" onClick={() => setCreatingUser(true)} className="mes-btn-primary">
                                <UserPlus className="mr-2 h-4 w-4" /> Neuer Benutzer
                            </Button>
                        </div>
                        <div className="grid gap-2 md:grid-cols-2">
                            {users.map((u) => (
                                <Card key={u.id} className="mes-card flex items-center justify-between p-3" data-testid={`admin-user-${u.id}`}>
                                    <div>
                                        <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-widest text-mes-muted">
                                            {u.role === "admin" ? <ShieldCheck className="h-3.5 w-3.5 text-mes-brand" /> : <UserIcon className="h-3.5 w-3.5" />}
                                            {u.role} {!u.is_active && <span className="text-mes-danger">deaktiviert</span>}
                                        </div>
                                        <div className="font-display font-semibold text-mes-ink">{u.name}</div>
                                        <div className="text-xs text-mes-muted tabular">{u.email}</div>
                                    </div>
                                    <Button variant="ghost" size="icon" onClick={() => setEditingUser(u)} data-testid={`btn-edit-user-${u.id}`}>
                                        <Pencil className="h-4 w-4" />
                                    </Button>
                                </Card>
                            ))}
                        </div>
                    </TabsContent>
                </Tabs>
            </main>

            <UserDialog
                open={creatingUser || !!editingUser}
                mode={editingUser ? "edit" : "create"}
                user={editingUser}
                onClose={() => { setCreatingUser(false); setEditingUser(null); }}
                onSaved={() => { setCreatingUser(false); setEditingUser(null); load(); }}
            />
            <ProjectDialog
                open={creatingProject || !!editingProject}
                mode={editingProject ? "edit" : "create"}
                project={editingProject}
                users={users}
                onClose={() => { setCreatingProject(false); setEditingProject(null); }}
                onSaved={() => { setCreatingProject(false); setEditingProject(null); load(); }}
            />
        </div>
    );
}

function ReportsPanel({ users, projects }) {
    const [filters, setFilters] = useState({
        project_id: "", user_id: "", activity: "", date_from: "", date_to: "",
    });
    const [summary, setSummary] = useState(null);

    const runReport = useCallback(async () => {
        try {
            const params = {};
            Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
            const { data } = await api.get("/reports/summary", { params });
            setSummary(data);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    }, [filters]);
    useEffect(() => { runReport(); }, [runReport]);

    const downloadCsv = async () => {
        const params = {};
        Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params[k] = v; });
        try {
            const res = await api.get("/reports/csv", { params, responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `zeiten_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (e) { toast.error("CSV-Export fehlgeschlagen"); }
    };

    return (
        <div className="space-y-4">
            <Card className="mes-card p-4">
                <div className="mb-3 font-display text-sm font-semibold text-mes-ink">Filter</div>
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                    <div>
                        <Label>Projekt</Label>
                        <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, project_id: v === "all" ? "" : v }))}>
                            <SelectTrigger data-testid="filter-project" className="h-10"><SelectValue placeholder="Alle" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle</SelectItem>
                                {projects.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Mitarbeiter</Label>
                        <Select value={filters.user_id || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, user_id: v === "all" ? "" : v }))}>
                            <SelectTrigger data-testid="filter-user" className="h-10"><SelectValue placeholder="Alle" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle</SelectItem>
                                {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Tätigkeit</Label>
                        <Select value={filters.activity || "all"} onValueChange={(v) => setFilters((f) => ({ ...f, activity: v === "all" ? "" : v }))}>
                            <SelectTrigger data-testid="filter-activity" className="h-10"><SelectValue placeholder="Alle" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Alle</SelectItem>
                                {ACTIVITIES.map((a) => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div><Label>Von</Label><Input data-testid="filter-from" type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} className="h-10" /></div>
                    <div><Label>Bis</Label><Input data-testid="filter-to" type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} className="h-10" /></div>
                </div>
                <div className="mt-3 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setFilters({ project_id: "", user_id: "", activity: "", date_from: "", date_to: "" })} data-testid="btn-reset-filters">
                        Zurücksetzen
                    </Button>
                    <Button onClick={downloadCsv} data-testid="btn-export-csv" className="mes-btn-primary">
                        <FileDown className="mr-2 h-4 w-4" /> CSV Export
                    </Button>
                </div>
            </Card>

            {summary && (
                <>
                    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                        <SummaryCard label="Gesamt" value={`${formatMinutesAsHours(summary.total_minutes)} h`} testid="summary-total" />
                        <SummaryCard label="Enthalten" value={`${formatMinutesAsHours(summary.included_minutes)} h`} tone="brand" testid="summary-included" />
                        <SummaryCard label="Zusatz" value={`${formatMinutesAsHours(summary.extra_minutes)} h`} tone="warn" testid="summary-extra" />
                        <SummaryCard label="Wert Zusatz" value={formatCurrency(summary.extra_value_eur)} tone="warn" testid="summary-value" />
                    </div>

                    <Card className="mes-card p-4">
                        <div className="mb-3 font-display text-sm font-semibold text-mes-ink">Nach Projekt</div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b border-mes-border text-left text-xs uppercase tracking-widest text-mes-muted">
                                        <th className="py-2">Projekt</th>
                                        <th className="py-2">Kunde</th>
                                        <th className="py-2 text-right">Kontingent</th>
                                        <th className="py-2 text-right">Geleistet</th>
                                        <th className="py-2 text-right">Enthalten</th>
                                        <th className="py-2 text-right">Zusatz</th>
                                        <th className="py-2 text-right">Wert</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.per_project.map((p) => (
                                        <tr key={p.project_id} className="border-b border-mes-border/70" data-testid={`report-row-${p.project_id}`}>
                                            <td className="py-2 font-medium text-mes-ink">{p.name}</td>
                                            <td className="py-2 text-mes-text">{p.customer}</td>
                                            <td className="py-2 text-right tabular">{p.allowance_hours.toFixed(2).replace(".", ",")} h</td>
                                            <td className="py-2 text-right tabular font-semibold">{formatMinutesAsHours(p.total_minutes)} h</td>
                                            <td className="py-2 text-right tabular text-mes-brand">{formatMinutesAsHours(p.included_minutes)} h</td>
                                            <td className="py-2 text-right tabular text-mes-warn">{formatMinutesAsHours(p.extra_minutes)} h</td>
                                            <td className="py-2 text-right tabular font-semibold text-mes-ink">{formatCurrency(p.extra_value_eur)}</td>
                                        </tr>
                                    ))}
                                    {summary.per_project.length === 0 && (
                                        <tr><td colSpan="7" className="py-4 text-center text-mes-muted">Keine Daten für diese Filter.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                </>
            )}
        </div>
    );
}

function SummaryCard({ label, value, tone = "default", testid }) {
    const cls = tone === "brand" ? "border-l-4 border-mes-brand"
        : tone === "warn" ? "border-l-4 border-mes-warn" : "border-l-4 border-mes-ink";
    return (
        <Card className={`mes-card p-3 ${cls}`} data-testid={testid}>
            <div className="text-[11px] font-medium uppercase tracking-widest text-mes-muted">{label}</div>
            <div className="mt-1 font-display text-xl font-bold text-mes-ink">{value}</div>
        </Card>
    );
}

function UserDialog({ open, mode, user, onClose, onSaved }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("mitarbeiter");
    const [password, setPassword] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (mode === "edit" && user) {
            setEmail(user.email); setName(user.name); setRole(user.role);
            setIsActive(user.is_active); setPassword("");
        } else {
            setEmail(""); setName(""); setRole("mitarbeiter"); setPassword(""); setIsActive(true);
        }
    }, [mode, user, open]);

    const save = async () => {
        setSaving(true);
        try {
            if (mode === "create") {
                await api.post("/users", { email, name, role, password });
                toast.success("Benutzer angelegt");
            } else {
                const payload = { name, role, is_active: isActive };
                if (password) payload.password = password;
                await api.put(`/users/${user.id}`, payload);
                toast.success("Aktualisiert");
            }
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="user-dialog">
                <DialogHeader><DialogTitle>{mode === "edit" ? "Benutzer bearbeiten" : "Neuer Benutzer"}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>E-Mail</Label>
                        <Input data-testid="user-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={mode === "edit"} className="h-11" />
                    </div>
                    <div>
                        <Label>Name</Label>
                        <Input data-testid="user-name" value={name} onChange={(e) => setName(e.target.value)} className="h-11" />
                    </div>
                    <div>
                        <Label>Rolle</Label>
                        <Select value={role} onValueChange={setRole}>
                            <SelectTrigger data-testid="user-role" className="h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="admin">Admin</SelectItem>
                                <SelectItem value="mitarbeiter">Mitarbeiter</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Passwort {mode === "edit" && <span className="text-xs text-mes-muted">(leer lassen zum Beibehalten)</span>}</Label>
                        <Input data-testid="user-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" />
                    </div>
                    {mode === "edit" && (
                        <div className="flex items-center justify-between rounded-md border border-mes-border p-3">
                            <div>
                                <div className="font-semibold">Aktiv</div>
                                <div className="text-xs text-mes-muted">Deaktivierte Nutzer können sich nicht anmelden.</div>
                            </div>
                            <Switch data-testid="user-active" checked={isActive} onCheckedChange={setIsActive} />
                        </div>
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={save} disabled={saving} data-testid="user-save" className="mes-btn-primary">Speichern</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function ProjectDialog({ open, mode, project, users, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: "", customer: "", address: "", start_date: "",
        status: "aktiv", stundenkontingent: 40, hourly_rate_extra: 65,
        assigned_user_ids: [],
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (mode === "edit" && project) {
            setForm({
                name: project.name || "",
                customer: project.customer || "",
                address: project.address || "",
                start_date: project.start_date || "",
                status: project.status || "aktiv",
                stundenkontingent: project.stundenkontingent || 0,
                hourly_rate_extra: project.hourly_rate_extra || 0,
                assigned_user_ids: project.assigned_user_ids || [],
            });
        } else {
            setForm({
                name: "", customer: "", address: "",
                start_date: new Date().toISOString().slice(0, 10),
                status: "aktiv", stundenkontingent: 40, hourly_rate_extra: 65,
                assigned_user_ids: [],
            });
        }
    }, [mode, project, open]);

    const toggleUser = (uid) => {
        setForm((f) => ({
            ...f,
            assigned_user_ids: f.assigned_user_ids.includes(uid)
                ? f.assigned_user_ids.filter((x) => x !== uid)
                : [...f.assigned_user_ids, uid],
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                ...form,
                stundenkontingent: Number(form.stundenkontingent),
                hourly_rate_extra: Number(form.hourly_rate_extra),
            };
            if (mode === "create") {
                await api.post("/projects", payload);
                toast.success("Projekt angelegt");
            } else {
                await api.put(`/projects/${project.id}`, payload);
                toast.success("Aktualisiert");
            }
            onSaved();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setSaving(false);
    };

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="project-dialog">
                <DialogHeader><DialogTitle>{mode === "edit" ? "Projekt bearbeiten" : "Neues Projekt"}</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                        <Label>Projektname</Label>
                        <Input data-testid="project-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11" />
                    </div>
                    <div>
                        <Label>Kunde</Label>
                        <Input data-testid="project-customer" value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} className="h-11" />
                    </div>
                    <div>
                        <Label>Status</Label>
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                            <SelectTrigger data-testid="project-status" className="h-11"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="aktiv">aktiv</SelectItem>
                                <SelectItem value="pausiert">pausiert</SelectItem>
                                <SelectItem value="abgeschlossen">abgeschlossen</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="col-span-2">
                        <Label>Adresse</Label>
                        <Input data-testid="project-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="h-11" />
                    </div>
                    <div><Label>Baubeginn</Label><Input data-testid="project-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-11" /></div>
                    <div><Label>Stundenkontingent (h)</Label><Input data-testid="project-hours" type="number" min="0" step="0.5" value={form.stundenkontingent} onChange={(e) => setForm({ ...form, stundenkontingent: e.target.value })} className="h-11" /></div>
                    <div><Label>Satz Zusatzstunden (€)</Label><Input data-testid="project-rate" type="number" min="0" step="0.5" value={form.hourly_rate_extra} onChange={(e) => setForm({ ...form, hourly_rate_extra: e.target.value })} className="h-11" /></div>
                    <div className="col-span-2">
                        <Label>Zugewiesene Mitarbeiter</Label>
                        <div className="grid gap-1 rounded-md border border-mes-border p-2 max-h-40 overflow-y-auto">
                            {users.map((u) => (
                                <label key={u.id} className="flex cursor-pointer items-center justify-between rounded px-2 py-1 hover:bg-mes-surfaceAlt">
                                    <span className="text-sm">{u.name} <span className="text-xs text-mes-muted">({u.role})</span></span>
                                    <input type="checkbox" data-testid={`project-assign-${u.id}`}
                                        checked={form.assigned_user_ids.includes(u.id)}
                                        onChange={() => toggleUser(u.id)}
                                        className="h-4 w-4 accent-mes-brand" />
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Abbrechen</Button>
                    <Button onClick={save} disabled={saving} data-testid="project-save" className="mes-btn-primary">Speichern</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
